import { chain } from 'icepick';
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import type { ThirdPartyFieldId, FieldId } from '../../../../common/constants/fields';
import type {
	State,
	ThirdPartyFieldConfigStateForItem,
	ThirdPartyFieldConfigState,
} from '../../../../common/types';

export type SetIsThirdPartyItemTypeFieldIncludedAction = (
	fieldId: ThirdPartyFieldId,
	isIncluded: boolean,
) => Action<State, void, void>;

export type SetIsThirdPartyFieldIncludedAction = (
	itemType: string | null,
	fieldId: ThirdPartyFieldId,
	isIncluded: boolean,
) => Action<State, void, void>;

export const getNewIncludedJiraFieldIds = (
	thirdPartyFieldConfigState: ThirdPartyFieldConfigStateForItem,
	thirdPartyFieldId: ThirdPartyFieldId,
	isIncluded: boolean,
): Set<FieldId> => {
	const newIncludedJiraFieldIds = new Set([...thirdPartyFieldConfigState.includedJiraFieldIds]);
	const prevMappedJiraField =
		thirdPartyFieldConfigState.thirdPartyFieldMappings[thirdPartyFieldId].mappedJiraField;
	const { isFieldDisabled } =
		thirdPartyFieldConfigState.thirdPartyFieldMappings[thirdPartyFieldId].mappingContext;
	// toggle OFF - make the field available for selection if field is not disabled
	if (!isIncluded && !isFieldDisabled && prevMappedJiraField.fieldId) {
		newIncludedJiraFieldIds.delete(prevMappedJiraField.fieldId);
	}
	// toggle ON - add mappedJiraField to mappedJiraFields
	if (isIncluded && prevMappedJiraField.fieldId) {
		newIncludedJiraFieldIds.add(prevMappedJiraField.fieldId);
	}
	return newIncludedJiraFieldIds;
};

export const getNewIncludedJiraFieldIdsWithItemType = (
	itemType: string,
	thirdPartyFieldConfigState: ThirdPartyFieldConfigState,
	thirdPartyFieldId: ThirdPartyFieldId,
	isIncluded: boolean,
): Set<FieldId> => {
	const newIncludedJiraFieldIds = new Set([
		...thirdPartyFieldConfigState.fieldMappingState[itemType].includedJiraFieldIds,
	]);
	const prevMappedJiraField =
		thirdPartyFieldConfigState.fieldMappingState[itemType].thirdPartyFieldMappings[
			thirdPartyFieldId
		].mappedJiraField;
	const { isFieldDisabled } =
		thirdPartyFieldConfigState.fieldMappingState[itemType].thirdPartyFieldMappings[
			thirdPartyFieldId
		].mappingContext;
	// toggle OFF - make the field available for selection if field is not disabled
	if (!isIncluded && !isFieldDisabled && prevMappedJiraField.fieldId) {
		newIncludedJiraFieldIds.delete(prevMappedJiraField.fieldId);
	}
	// toggle ON - add mappedJiraField to mappedJiraFields
	if (isIncluded && prevMappedJiraField.fieldId) {
		newIncludedJiraFieldIds.add(prevMappedJiraField.fieldId);
	}
	return newIncludedJiraFieldIds;
};

export const setIsThirdPartyFieldIncluded: SetIsThirdPartyFieldIncludedAction =
	(itemType: string | null, fieldId: ThirdPartyFieldId, isIncluded: boolean) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const state = getState();

		if (itemType == null || state.thirdPartyState.thirdPartyFieldConfigState === null) {
			return;
		}
		const { fieldMappingState } = state.thirdPartyState.thirdPartyFieldConfigState;

		const updatedFieldMappingContext = {
			...fieldMappingState[itemType].thirdPartyFieldMappings[fieldId].mappingContext,
			isIncluded,
		};

		const newState = chain(state)
			.setIn(
				[
					'thirdPartyState',
					'thirdPartyFieldConfigState',
					'fieldMappingState',
					itemType,
					'thirdPartyFieldMappings',
					fieldId,
					'mappingContext',
				],
				updatedFieldMappingContext,
			)
			.setIn(
				[
					'thirdPartyState',
					'thirdPartyFieldConfigState',
					'fieldMappingState',
					itemType,
					'includedJiraFieldIds',
				],
				getNewIncludedJiraFieldIdsWithItemType(
					itemType,
					state.thirdPartyState.thirdPartyFieldConfigState,
					fieldId,
					isIncluded,
				),
			)
			.value();
		setState(newState);
	};
